import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { QEStrategydataFlowContext } from "../../../main-pages/QEStrategyMainPage";
import QEStrategyCommonButton from "../qe-strategy-homepage/CommonButton";
import { usePrefillForm } from "../../../../utils/qe-strategy-utils/usePreFillForm";

const ObservabilityForm = ({ onBack, onNext }) => {
  // --- State Variables ---
  const [qualityMonitoring, setQualityMonitoring] = useState("");
  const [sloDefined, setSloDefined] = useState("");
  const [testResultsLinked, setTestResultsLinked] = useState("");
  const [feedbackLoops, setFeedbackLoops] = useState("");
  const [dashboardsAvailable, setDashboardsAvailable] = useState("");
  const [securityRequirements, setSecurityRequirements] = useState("");
  const [resilienceDesign, setResilienceDesign] = useState("");
  const [multiTenant, setMultiTenant] = useState("");
  const [chaosScenarios, setChaosScenarios] = useState("");
  const [dataTested, setDataTested] = useState("");
  const [schemaDrift, setSchemaDrift] = useState("");
  const {
    observabilityResilienceSecurityAndScalability,
    setObservabilityResilienceSecurityAndScalability,
    dataFromFileUploads,
  } = useContext(QEStrategydataFlowContext);

  // --- Handlers ---
  const handleQualityMonitoringChange = (e) =>
    setQualityMonitoring(e.target.value);
  const handleSloDefinedChange = (e) => setSloDefined(e.target.value);
  const handleTestResultsLinkedChange = (e) =>
    setTestResultsLinked(e.target.value);
  const handleFeedbackLoopsChange = (e) => setFeedbackLoops(e.target.value);
  const handleDashboardsAvailableChange = (e) =>
    setDashboardsAvailable(e.target.value);
  const handleSecurityRequirementsChange = (e) =>
    setSecurityRequirements(e.target.value);
  const handleResilienceDesignChange = (e) =>
    setResilienceDesign(e.target.value);
  const handleMultiTenantChange = (e) => setMultiTenant(e.target.value);
  const handleChaosScenariosChange = (e) => setChaosScenarios(e.target.value);
  const handledataTestedChange = (e) => setDataTested(e.target.value);
  const handleSchemaDriftChange = (e) => setSchemaDrift(e.target.value);



  useEffect(() => {
  if (observabilityResilienceSecurityAndScalability) {
    setQualityMonitoring(observabilityResilienceSecurityAndScalability.qualityMonitoring || "");
    setSloDefined(observabilityResilienceSecurityAndScalability.sloDefined || "");
    setTestResultsLinked(observabilityResilienceSecurityAndScalability.testResultsLinked || "");
    setFeedbackLoops(observabilityResilienceSecurityAndScalability.feedbackLoops || "");
    setDashboardsAvailable(observabilityResilienceSecurityAndScalability.dashboardsAvailable || "");
    setSecurityRequirements(observabilityResilienceSecurityAndScalability.securityRequirements || "");
    setResilienceDesign(observabilityResilienceSecurityAndScalability.resilienceDesign || "");
    setMultiTenant(observabilityResilienceSecurityAndScalability.multiTenant || "");
    setChaosScenarios(observabilityResilienceSecurityAndScalability.chaosScenarios || "");
    setDataTested(observabilityResilienceSecurityAndScalability.dataTested || "");
    setSchemaDrift(observabilityResilienceSecurityAndScalability.schemaDrift || "");
  }
}, [observabilityResilienceSecurityAndScalability]);

  const observabilityResilienceSecurityDataObject = {
    qualityMonitoring,
    sloDefined,
    testResultsLinked,
    feedbackLoops,
    dashboardsAvailable,
    securityRequirements,
    resilienceDesign,
    multiTenant,
    chaosScenarios,
    dataTested,
    schemaDrift,
  };

  const handleBack = () => {
    onBack();
  };

  const handleNext = () => {
    setObservabilityResilienceSecurityAndScalability(
      observabilityResilienceSecurityDataObject
    );
    onNext();
  };

  const questionToFieldMap = {
    "1. Are live metrics or telemetry used for quality monitoring?":
      "qualityMonitoring",
    "2. Are SLOs/OKRs defined for quality engineering?": "sloDefined",
    "3. Are test results linked to defect and incident data?":
      "testResultsLinked",
    "4. Are retrospectives or feedback loops used to review quality signals?":
      "feedbackLoops",
    "5. Are dashboards available to track test gaps vs user behavior?":
      "dashboardsAvailable",
    "6. What are the security requirements?": "securityRequirements",
    "7. Is the system designed for resilience and high availability?":
      "resilienceDesign",
    "8. Is it multi-tenant or geo-distributed?": "multiTenant",
    "9. Are failover, fallback, or chaos scenarios tested regularly?":
      "chaosScenarios",
    "10. Are freshness, accuracy, and availability of data tested?":
      "dataTested",
    "11. Is schema drift and referential integrity validated as part of testing?":
      "schemaDrift",
  };

  const [multiTenantOptions, setMultiTenantOptions] = useState([
    "Multi-tenant",
    "Geo-distributed",
    "Both",
    "Neither",
  ]);

  usePrefillForm(
    dataFromFileUploads,
    questionToFieldMap,
    {
      qualityMonitoring: setQualityMonitoring,
      sloDefined: setSloDefined,
      testResultsLinked: setTestResultsLinked,
      feedbackLoops: setFeedbackLoops,
      dashboardsAvailable: setDashboardsAvailable,
      securityRequirements: setSecurityRequirements,
      resilienceDesign: setResilienceDesign,
      multiTenant: setMultiTenant,
      chaosScenarios: setChaosScenarios,
      dataTested: setDataTested,
      schemaDrift: setSchemaDrift,
    },
    null,
    {
      multiTenant: {
        options: multiTenantOptions,
        setOptions: setMultiTenantOptions,
      },
    }
  );

  return (
    <Box
      sx={{
        width: {
          xs: "100%", // full width on small screens
          sm: "100%",
          md: "1030px", // fixed width on medium and above
        },
        mx: "auto", // center horizontally
        mt: { xs: 5, sm: 8, md: 3 },
        mb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 }, // side padding only for smaller screens
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Title */}
        <Typography variant="h6" gutterBottom sx={{ fontSize: 20, position: 'relative', top: '-14px'}}>
          Observability, Resilience, Security & Scalability
        </Typography>

        <Grid container spacing={3}>
          {/* Quality Monitoring */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are live metrics or telemetry used for quality monitoring?
              </FormLabel>
              <RadioGroup
                row
                value={qualityMonitoring}
                onChange={handleQualityMonitoringChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* SLOs/OKRs */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are SLOs/OKRs defined for quality engineering?
              </FormLabel>
              <RadioGroup
                row
                value={sloDefined}
                onChange={handleSloDefinedChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Test Results Linked */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are test results linked to defect and incident data?
              </FormLabel>
              <RadioGroup
                row
                value={testResultsLinked}
                onChange={handleTestResultsLinkedChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Feedback Loops */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are retrospectives or feedback loops used to review quality
                signals?
              </FormLabel>
              <RadioGroup
                row
                value={feedbackLoops}
                onChange={handleFeedbackLoopsChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Dashboards */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are dashboards available to track test gaps vs user behavior?
              </FormLabel>
              <RadioGroup
                row
                value={dashboardsAvailable}
                onChange={handleDashboardsAvailableChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Security Requirements */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>What are the security requirements?</FormLabel>
              <RadioGroup
                row
                value={securityRequirements}
                onChange={handleSecurityRequirementsChange}
              >
                <FormControlLabel
                  value="Basic"
                  control={<Radio />}
                  label="Basic"
                />
                <FormControlLabel
                  value="Moderate"
                  control={<Radio />}
                  label="Moderate"
                />
                <FormControlLabel
                  value="High"
                  control={<Radio />}
                  label="High"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Resilience */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Is the system designed for resilience and high availability?
              </FormLabel>
              <RadioGroup
                row
                value={resilienceDesign}
                onChange={handleResilienceDesignChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth  variant="outlined">
              <InputLabel
                shrink
                id="multi-tenant-label"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  mb: 0.5,
                }}
              >
                Is it multi-tenant or geo-distributed?
              </InputLabel>
              <Select
                labelId="multi-tenant-label"
                value={multiTenant}
                onChange={handleMultiTenantChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>Select</span>;
                  }
                  return selected;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200, // ✅ scrollbar if too tall
                      width: 300,     // ✅ fixed width 300px
                    },
                  },
                  disablePortal: false,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "flip",
                        enabled: false,
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                        },
                      },
                    ],
                  },
                }}
              >
                {multiTenantOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are failover, fallback, or chaos scenarios tested regularly?
              </FormLabel>
              <RadioGroup
                row
                value={chaosScenarios}
                onChange={handleChaosScenariosChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are freshness, accuracy, and availability of data tested?
              </FormLabel>
              <RadioGroup
                row
                value={dataTested}
                onChange={handledataTestedChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Is schema drift and referential integrity validated as part of
                testing?
              </FormLabel>
              <RadioGroup
                row
                value={schemaDrift}
                onChange={handleSchemaDriftChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Buttons outside Paper */}
      <QEStrategyCommonButton handleBack={handleBack} handleNext={handleNext} />
    </Box>
  );
};

export default ObservabilityForm;
